/**
 * Structured data for the louvered pergola pages.
 *
 * Emits, per pergola URL:
 *   - Service (via serviceHeadScripts) + BreadcrumbList
 *   - ONE merged FAQPage covering both the head-only Q&A and the Q&A that is
 *     visible on the page (Google requires the answer text to be on-page, and
 *     requires a single FAQPage node per URL)
 *   - Organization + LocalBusiness for Texas Made Shade (shared `@id`s, so
 *     engines merge instead of duplicating)
 *
 * Because a single FAQPage is emitted here, pergola routes pass
 * `hideFaqJsonLd` to <ServicePage /> so the in-body FAQ block does not emit a
 * second, competing FAQPage node.
 */

import { canonicalUrl, SITE_URL } from "@/lib/seo";
import { businessHeadScripts, BUSINESS_ID, ORG_ID } from "@/lib/businessJsonLd";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";

export type Faq = { q: string; a: string };

/** Ties this page's Organization to the louvered pergola service offering. */
export function pergolaPartnerOrganizationJsonLd(path: string) {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    url: SITE_URL,
    mainEntityOfPage: canonicalUrl(path),
    makesOffer: {
      "@type": "Offer",
      url: canonicalUrl(path),
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "USD",
        price: 35000,
        valueAddedTaxIncluded: false,
        description: "Starting investment for a motorized louvered pergola; final price is quoted per project.",
      },
      itemOffered: {
        "@type": "Service",
        name: "Motorized Louvered Pergola Installation",
        provider: { "@id": BUSINESS_ID },
      },
      availableAtOrFrom: { "@id": BUSINESS_ID },
      areaServed: [
        { "@type": "City", name: "San Antonio" },
        { "@type": "City", name: "Austin" },
        { "@type": "State", name: "Texas" },
      ],
    },

  };
}

function dedupeFaqs(faqs: Faq[]) {
  const seen = new Set<string>();
  return faqs.filter((f) => {
    const key = f.q.trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** One FAQPage node for the URL, merging head-level and on-page Q&A. */
export function pergolaFaqPageJsonLd(path: string, faqs: Faq[]) {
  const url = canonicalUrl(path);
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${url}#faq`,
    url,
    inLanguage: "en-US",
    isPartOf: { "@id": `${SITE_URL}/#website` },
    publisher: { "@id": ORG_ID },
    mainEntity: dedupeFaqs(faqs).map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}

export type Crumb = { name: string; path: string };

/** The louvered pergola hub — parent crumb for every louvered city page. */
export const PERGOLA_HUB_CRUMB: Crumb = {
  name: "Louvered Pergolas",
  path: "/louvered-pergolas",
};

/**
 * Hierarchical BreadcrumbList for a pergola page:
 *   Home → [ancestors…] → Current page
 *
 * Google needs the full trail (not just Home → page) to render a breadcrumb
 * path in the SERP instead of the bare URL.
 */
export function pergolaBreadcrumbJsonLd(path: string, name: string, ancestors: Crumb[] = []) {
  const url = canonicalUrl(path);
  const trail: Crumb[] = [
    { name: "Home", path: "/" },
    ...ancestors.filter((c) => c.path !== path),
    { name, path },
  ];

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "@id": `${url}#breadcrumb`,
    itemListElement: trail.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      item: crumb.path === "/" ? `${SITE_URL}/` : canonicalUrl(crumb.path),
    })),
  };
}

export function pergolaBreadcrumbScript(path: string, name: string, ancestors: Crumb[] = []) {
  return {
    type: "application/ld+json",
    children: JSON.stringify(pergolaBreadcrumbJsonLd(path, name, ancestors)),
  };
}

interface PergolaSchemaInput {
  path: string;
  serviceName: string;
  serviceType?: string;
  breadcrumbName?: string;
  description: string;
  /** Q&A rendered in the head-only block. */
  faqs: Faq[];
  /** Q&A visible in the page's FAQ section — merged into the same FAQPage. */
  visibleFaqs?: Faq[];
  /** Ancestor crumbs between Home and this page (e.g. the pergola hub). */
  breadcrumbAncestors?: Crumb[];
}

/**
 * Full head `scripts` array for a louvered pergola page: Service, single
 * FAQPage, hierarchical BreadcrumbList, Organization and LocalBusiness.
 */
export function pergolaHeadScripts(input: PergolaSchemaInput) {
  const allFaqs = dedupeFaqs([...(input.visibleFaqs ?? []), ...input.faqs]);

  // serviceHeadScripts returns [Service, FAQPage, BreadcrumbList] — drop its
  // FAQPage (merged below) and its flat breadcrumb (replaced by the
  // hierarchical one) so only one node of each type is emitted per URL.
  const [service] = serviceHeadScripts({
    path: input.path,
    serviceName: input.serviceName,
    serviceType: input.serviceType,
    breadcrumbName: input.breadcrumbName,
    description: input.description,
    faqs: allFaqs,
  });

  return [
    service,
    pergolaBreadcrumbScript(
      input.path,
      input.breadcrumbName ?? input.serviceName,
      input.breadcrumbAncestors ?? [],
    ),
    // Only Q&A that is actually visible on the page is eligible for FAQ rich
    // results, so the FAQPage node uses the on-page set when one is provided.
    {
      type: "application/ld+json",
      children: JSON.stringify(
        pergolaFaqPageJsonLd(input.path, input.visibleFaqs?.length ? dedupeFaqs(input.visibleFaqs) : allFaqs),
      ),
    },
    ...businessHeadScripts(),
    { type: "application/ld+json", children: JSON.stringify(pergolaPartnerOrganizationJsonLd(input.path)) },
  ];
}

